import React, { useState } from "react";

import Modal from "../ui/modal";
import { collectLocalData, exportLocalData } from "./localData";

/**
 * What logging out costs, said before it is paid.
 * ---------------------------------------------------------------------------
 * Everything someone has set here lives in this browser: the sizes, the
 * palette, the rail's order and the names on it, which stage drew last, how
 * wide a column was left. Logging out clears it, and a preference that takes
 * two minutes to set and goes without a word is a product that cannot be
 * trusted with the next one.
 *
 * So: what will go, how much of it there is, and the chance to take it with
 * you first. The file holds the preferences and not the keys - see localData.
 * ---------------------------------------------------------------------------
 */
export default function LogoutWarning({ open, onClose, onLogout, isDarkMode }) {
  const [saved, setSaved] = useState(0);
  const { kept } = collectLocalData();

  const ink = isDarkMode ? "rgba(245,239,230,0.92)" : "rgba(17,17,17,0.9)";
  const dim = isDarkMode ? "rgba(245,239,230,0.55)" : "rgba(17,17,17,0.55)";
  const hair = isDarkMode ? "rgba(245,239,230,0.14)" : "rgba(17,17,17,0.12)";
  const surface = isDarkMode ? "#141414" : "#ffffff";

  return (
    <Modal open={open} onClose={onClose} close={false} className="logout-warning">
      <div style={{
        boxSizing: "border-box",
        width: "min(520px, calc(100vw - 48px))",
        padding: "24px 26px 22px",
        border: `1px solid ${hair}`,
        borderRadius: 14,
        background: surface,
        color: ink,
        fontFamily: "Inter, system-ui, -apple-system, sans-serif",
        boxShadow: "0 28px 64px rgba(0,0,0,0.45)",
      }}>
        <h3 style={{ margin: "0 0 10px", fontSize: 17, fontWeight: 600 }}>Log out of AppFactor?</h3>

        <p style={{ margin: "0 0 14px", fontSize: 13.5, lineHeight: 1.6, color: dim }}>
          What you have set in this browser goes with the session: the sizes and
          the palette, the rail&apos;s order and the names on it, and every other
          preference this machine was keeping — {kept} {kept === 1 ? "entry" : "entries"} in all.
          Nothing in the estate itself is touched.
        </p>

        <div style={{
          display: "flex", alignItems: "center", gap: 12,
          padding: "12px 14px", marginBottom: 18,
          border: `1px solid ${hair}`, borderRadius: 10,
        }}>
          <span style={{ flex: "1 1 auto", fontSize: 12.5, lineHeight: 1.5, color: dim }}>
            {saved
              ? `Saved ${saved} ${saved === 1 ? "entry" : "entries"} to your downloads. Keys and tokens were left out.`
              : "Take them with you as a file first — keys and tokens are left out of it."}
          </span>
          <button
            type="button"
            onClick={() => setSaved(exportLocalData())}
            style={{
              flex: "0 0 auto", height: 30, padding: "0 14px", borderRadius: 8,
              border: `1px solid ${hair}`, background: "transparent", color: ink,
              fontFamily: "inherit", fontSize: 12.5, cursor: "pointer",
            }}
          >
            {saved ? "Export again" : "Export settings"}
          </button>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              height: 34, padding: "0 16px", borderRadius: 9,
              border: `1px solid ${hair}`, background: "transparent", color: ink,
              fontFamily: "inherit", fontSize: 13, cursor: "pointer",
            }}
          >
            Stay
          </button>
          <button
            type="button"
            onClick={onLogout}
            style={{
              height: 34, padding: "0 18px", borderRadius: 9,
              border: "1px solid rgb(246,64,13)", background: "rgb(246,64,13)", color: "#fff",
              fontFamily: "inherit", fontSize: 13, fontWeight: 500, cursor: "pointer",
            }}
          >
            Log out
          </button>
        </div>
      </div>
    </Modal>
  );
}
